import {FavoriteCoordinator} from "../../features/favorites/coordinator";
import {favoritePorts} from "../../features/favorites/client";
import {decodeTarget} from "../../features/favorites/decoder";
import {FavoriteError, type FavoriteErrorCode, type FavoriteTarget} from "../../features/favorites/model";
import {sessionStore, SessionOwnerChangedError, type SessionStore} from "./session-store";

// Notifications carry no private payload. Content imports this capability from
// the stable /assets/auth-loader.js entry, sharing the auth island's store.
export interface BrowserSessionSnapshot {
  readonly status: "loading" | "anonymous" | "authenticated" | "error";
  readonly epoch: number;
  readonly readOnly: boolean;
}
export interface FavoriteCommandSnapshot {
  readonly phase: "idle" | "sending" | "uncertain" | "reconciling";
  readonly error?: {readonly code: FavoriteErrorCode; readonly requestId: string};
}
export interface BrowserFavoriteCommands {
  snapshot(): FavoriteCommandSnapshot;
  subscribe(listener: () => void): () => void;
  begin(operation: "add" | "remove", target: FavoriteTarget): Promise<void>;
  retry(): Promise<void>;
  refresh(target: FavoriteTarget): Promise<void>;
  dispose(): void;
}
export interface BrowserOwnerLease {
  readonly readOnly: boolean;
  readonly signal: AbortSignal;
  isCurrent(): boolean;
  request(input: string, init?: RequestInit): Promise<Response>;
  favorites(reconcile: (target: FavoriteTarget, signal: AbortSignal) => Promise<void>): BrowserFavoriteCommands;
  dispose(): void;
}
export interface BrowserSession {
  snapshot(): BrowserSessionSnapshot;
  subscribe(listener: () => void): () => void;
  captureOwner(): BrowserOwnerLease | null;
}

function isolate(action: () => void): void {
  try { action(); } catch {
    // A consumer exception must not interrupt synchronous owner invalidation
    // or prevent other consumers from clearing their private state.
  }
}
function notify(listeners: Set<() => void>): void {
  for (const listener of [...listeners]) isolate(listener);
}

export function createBrowserSession(session: SessionStore): BrowserSession {
  const listeners = new Set<() => void>();
  const leases = new Set<{current: () => boolean; dispose: () => void}>();
  const value = (): BrowserSessionSnapshot => Object.freeze({status: session.snapshot().status, epoch: session.ownerEpoch(), readOnly: session.snapshot().status === "authenticated" && session.captureOwner().impersonationId !== undefined});
  let snapshot = value();
  session.subscribe(() => {
    // Abort/clear capabilities before notifying consumers to clear their DOM.
    for (const lease of [...leases]) if (!lease.current()) isolate(lease.dispose);
    const next = value();
    if (next.status === snapshot.status && next.epoch === snapshot.epoch && next.readOnly === snapshot.readOnly) return;
    snapshot = next; notify(listeners);
  });
  return Object.freeze({
    snapshot: () => snapshot,
    subscribe: (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; },
    captureOwner: (): BrowserOwnerLease | null => {
      if (session.snapshot().status !== "authenticated") return null;
      const owner = session.captureOwner(); const controller = new AbortController(); const commands = new Set<BrowserFavoriteCommands>();
      const current = () => !controller.signal.aborted && session.isCurrentOwner(owner);
      const dispose = () => {
        controller.abort();
        for (const command of [...commands]) isolate(command.dispose);
        commands.clear(); leases.delete(record);
      };
      const record = {current, dispose}; leases.add(record);
      const guard = () => { if (!current()) throw new SessionOwnerChangedError(); };
      return Object.freeze({
        readOnly: owner.impersonationId !== undefined, signal: controller.signal, isCurrent: current, dispose,
        request: async (input: string, init: RequestInit = {}) => {
          guard(); const url = new URL(input, window.location.origin);
          if (url.origin !== window.location.origin || !url.pathname.startsWith("/api/v1/") || url.username || url.password) throw new FavoriteError("validation");
          // The lease signal always fences/aborts this owner, even when the
          // consumer also supplies a shorter navigation/request lifetime.
          const signal = init.signal ? AbortSignal.any([controller.signal, init.signal]) : controller.signal;
          const response = await session.authorizedOwnerFetch(owner, url, {...init, signal}); guard(); return response;
        },
        favorites: (reconcile: (target: FavoriteTarget, signal: AbortSignal) => Promise<void>) => {
          guard();
          const model = new FavoriteCoordinator(owner, favoritePorts(owner, session), session, undefined, async (target, signal) => {
            guard(); await reconcile(target, signal); guard();
          });
          const disconnect = model.connect(); const observers = new Set<() => void>();
          const project = (): FavoriteCommandSnapshot => {
            const state = model.snapshot(); const error = state.pending?.error ?? state.failure;
            return Object.freeze({phase: state.pending?.phase ?? "idle", ...(error ? {error: Object.freeze({code: error.code, requestId: error.requestId})} : {})});
          };
          let commandSnapshot = project(); let disposed = false;
          const unsubscribe = model.subscribe(() => { commandSnapshot = project(); notify(observers); });
          const command: BrowserFavoriteCommands = Object.freeze({
            snapshot: () => commandSnapshot,
            subscribe: (listener: () => void) => { observers.add(listener); return () => { observers.delete(listener); }; },
            begin: async (operation: "add" | "remove", target: FavoriteTarget) => {
              guard(); if (disposed) throw new SessionOwnerChangedError();
              if (operation !== "add" && operation !== "remove") throw new FavoriteError("validation");
              await model.begin(operation, decodeTarget(target));
            },
            retry: async () => { guard(); if (disposed) throw new SessionOwnerChangedError(); await model.retry(); },
            refresh: async (target: FavoriteTarget) => { guard(); if (disposed) throw new SessionOwnerChangedError(); await model.refresh(decodeTarget(target)); },
            dispose: () => {
              if (disposed) return; disposed = true;
              isolate(disconnect); isolate(unsubscribe);
              observers.clear(); commands.delete(command);
            },
          });
          commands.add(command); return command;
        },
      });
    },
  });
}
export const browserSession = createBrowserSession(sessionStore);
